import { useCallback } from "react";
import type { CalendarDate } from "@internationalized/date";
import { parseDate, today } from "@internationalized/date";
import {
  useStateLocalStorage,
  type Codec,
} from "./hooks/use-localstorage-state";

export type Route = {
  departureDate: CalendarDate | undefined;
  destination: string | undefined;
  origin: string | undefined;
  returnDate?: CalendarDate | undefined;
};

const EMPTY_ROUTE = {
  departureDate: undefined,
  destination: "",
  origin: "",
};

const routesCodec: Codec<readonly [Route, ...Route[]]> = {
  decode: (raw: string): [Route, ...Route[]] => {
    const array = JSON.parse(raw) as Omit<
      Route,
      "departureDate" | "returnDate"
    > &
      {
        departureDate?: string;
        returnDate?: string;
      }[];

    return array.map((s) => {
      let departureDate = s.departureDate
        ? parseDate(s.departureDate)
        : undefined;
      let returnDate = s.returnDate ? parseDate(s.returnDate) : undefined;
      const td = today(Intl.DateTimeFormat().resolvedOptions().timeZone);

      if (
        (departureDate && departureDate.compare(td) <= 0) ||
        (returnDate && returnDate.compare(td) <= 0)
      ) {
        departureDate = undefined;
        returnDate = undefined;
      }
      return {
        ...s,
        departureDate,
        returnDate,
      } as Route;
    }) as [Route, ...Route[]];
  },
  encode: (state) =>
    JSON.stringify(
      state.map((s) => ({
        ...s,
        departureDate: s.departureDate?.toString(),
        returnDate: s.returnDate?.toString(),
      })),
    ),
};

export type UseFormProperties = {
  minRoutes: number;
  maxRoutes?: number;
  initialRoutes?: [Route, ...Route[]];
  startDate?: CalendarDate | undefined;
  endDate?: CalendarDate | undefined;
};
export const useForm = ({
  minRoutes = 1,
  maxRoutes = Infinity,
  initialRoutes = [EMPTY_ROUTE, EMPTY_ROUTE],
  startDate = today(Intl.DateTimeFormat().resolvedOptions().timeZone),
  endDate = today(Intl.DateTimeFormat().resolvedOptions().timeZone).add({
    days: 331,
  }),
}: UseFormProperties) => {
  const [routes, setRoutes] = useStateLocalStorage<
    readonly [Route, ...Route[]]
  >("routes", initialRoutes, routesCodec);
  const [promoCode, setPromoCode] = useStateLocalStorage<string>(
    "promo-code",
    "",
  );
  const [usePoints, setUsePoints] = useStateLocalStorage<boolean>(
    "use-points",
    false,
  );

  const canAdd = routes.length < maxRoutes;
  const canRemove = routes.length > minRoutes;
  const add = useCallback(
    (route?: Route) => {
      setRoutes((routes) =>
        routes.length < maxRoutes ? [...routes, route ?? EMPTY_ROUTE] : routes,
      );
    },
    [maxRoutes],
  );
  const remove = useCallback(
    (index: number) => {
      setRoutes((routes) =>
        routes.length > minRoutes
          ? (routes.toSpliced(index, 1) as [Route, ...Route[]])
          : routes,
      );
    },
    [minRoutes],
  );
  const insert = useCallback(
    (index: number, route?: Route) => {
      setRoutes((routes) =>
        index < maxRoutes
          ? (routes.toSpliced(index, 0, route ?? EMPTY_ROUTE) as [
              Route,
              ...Route[],
            ])
          : routes,
      );
    },
    [maxRoutes],
  );
  const modify = useCallback(
    <K extends keyof Route>(index: number, key: K, value: Route[K]) => {
      setRoutes((routes) => {
        const currentRoute = routes[index];
        return currentRoute
          ? (routes.with(index, { ...currentRoute, [key]: value }) as [
              Route,
              ...Route[],
            ])
          : routes;
      });
    },
    [],
  );
  const swapOriginDestination = useCallback((index: number) => {
    setRoutes((routes) => {
      const currentRoute = routes[index];
      return currentRoute
        ? (routes.with(index, {
            ...currentRoute,
            destination: currentRoute.origin,
            origin: currentRoute.destination,
          }) as [Route, ...Route[]])
        : routes;
    });
  }, []);

  return {
    add,
    canAdd,
    canRemove,
    endDate,
    first: routes[0],
    insert,
    maxRoutes,
    minRoutes,
    modify,
    promoCode,
    remove,
    routes,
    setPromoCode,
    setUsePoints,
    startDate,
    swapOriginDestination,
    usePoints,
  };
};
